import { useTranslation } from "react-i18next"

import type { MessagesFigures } from "@/api/generated/model"
import { cn } from "@/lib/cn"
import { formatCount, formatPercent, localeOf } from "@/lib/format"

const ROWS = [
  { id: "delivered", bar: "bg-success" },
  { id: "failed", bar: "bg-destructive" },
  { id: "notFinal", bar: "bg-muted-foreground/50" },
] as const

/**
 * FR-DSH-OVW-05: what became of what was sent. "Not final" is sent minus
 * delivered minus failed; unknown is not split out (DW-32).
 */
export function StatusBreakdown({ figures }: { figures: MessagesFigures }) {
  const { t, i18n } = useTranslation("overview")
  const locale = localeOf(i18n.language)
  const sent = figures.sent.current
  if (sent === 0) return <p className="text-sm text-muted-foreground">{t("breakdown.empty")}</p>

  const delivered = figures.delivered.current
  const failed = figures.failed.current
  const counts = { delivered, failed, notFinal: Math.max(0, sent - delivered - failed) }

  return (
    <ul className="space-y-3">
      {ROWS.map((row) => {
        const n = counts[row.id]
        return (
          <li key={row.id} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span>{t(`breakdown.${row.id}`)}</span>
              <span className="numeric text-muted-foreground">
                {formatCount(n, locale)} · {formatPercent(n / sent, locale)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
              <div
                className={cn("h-full rounded-full", row.bar)}
                style={{ width: `${String((n / sent) * 100)}%` }}
              />
            </div>
            {row.id === "notFinal" ? (
              <p className="text-xs text-muted-foreground">{t("breakdown.notFinalHint")}</p>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
